<template>
  <i-drawer
    :visible="show"
    class="collect-session-modal"
    title="Collect"
    :subtitle="modalSubheading"
    @hide-drawer="hideCollectModal"
  >
    <div
      id="collect-modal-content"
      class="d-flex flex-column justify-content-between"
    >
      <div v-if="!collectModalLoading">
        <div v-if="!collectSession">
          <div class="i-mb-10">
            <i-icon-toggle
              :options="toggleOptions"
              @toggle-selected="toggleEmail"
            ></i-icon-toggle>
          </div>

          <div v-if="collectSendEmail" class="i-mb-11">
            <i-text-input
              id="collect-email"
              :model-value="collectEmail"
              class="i-my-10"
              label="Customer Email"
              placeholder="customer@example.com"
              data-testid="workspace-name-field"
              :debounce-time="200"
              :has-error="collectSendEmail && collectEmailHasErrors"
              :show-hint-text="collectEmailHasErrors"
              :hint-text="
                collectSendEmail && collectEmailHasErrors
                  ? 'Must be a valid email address'
                  : null
              "
              @update:model-value="collectEmail = $event"
            ></i-text-input>
          </div>

          <div id="document-requests" class="document-requests">
            <div v-if="hasDocumentRequests">
              <div
                class="document-requests__heading d-flex align-items-center justify-content-between i-my-9"
              >
                <div
                  class="heading-tooltip d-flex flex-grow-1 align-items-center"
                >
                  <h5
                    class="i-text-heading-5 i-text-neutral-120 i-m-0 i-mr-4"
                    @click="toggleCollapse('collapseDocumentRequestSection')"
                  >
                    Documents
                  </h5>
                  <info-icon
                    title="Specify what documents you'd like your customer to upload"
                  />
                </div>

                <div class="actions d-flex">
                  <i-button
                    id="collect-tags-remove-button"
                    class="remove"
                    :left-icon="'medium-remove-1'"
                    :size="iButtonSizes.LARGE"
                    :type="iButtonTypes.NEUTRAL"
                    :button-style="iButtonStyles.GHOST"
                    @button-click="removeAllDocRequests"
                  />
                  <i-tooltip
                    target="#collect-tags-remove-button"
                    title="Remove"
                    :alignment="iTooltipAlignmentMode.TOP_MIDDLE"
                  />

                  <i-button
                    id="collect-tags-collapse-button"
                    :left-icon="'medium-chevron-down'"
                    :size="iButtonSizes.LARGE"
                    :type="iButtonTypes.NEUTRAL"
                    :button-style="iButtonStyles.GHOST"
                    :class="{
                      collapsed: collapseDocumentRequestSection,
                    }"
                    @button-click="
                      toggleCollapse('collapseDocumentRequestSection')
                    "
                  />
                  <i-tooltip
                    target="#collect-tags-collapse-button"
                    title="Collapse"
                    :alignment="iTooltipAlignmentMode.TOP_MIDDLE"
                  />
                </div>
              </div>

              <div
                v-if="!collapseDocumentRequestSection"
                class="document-requests__content"
              >
                <div
                  v-for="(document, documentIndex) in documents"
                  :key="documentIndex"
                  :draggable="true"
                  class="d-flex flex-column i-mb-11 sortable"
                  @dragstart="onDragStart(documentIndex)"
                  @dragover="onDragOver($event, documentIndex)"
                  @drop="onDrop($event, documentIndex)"
                >
                  <div class="drag-handle">
                    <i-icon
                      symbol="small-drag-handle-vertical"
                      :size="16"
                      class="i-mr-2 i-mt-12"
                    />
                  </div>
                  <div
                    :id="`document-${documentIndex}`"
                    class="d-flex w-100 align-items-end"
                  >
                    <div class="flex-grow-1">
                      <i-text-input
                        :id="`document-label-${documentIndex}`"
                        label="Document Title"
                        placeholder="e.g. Bank Statement"
                        :debounce-time="200"
                        :model-value="document.label"
                        :has-error="!documentHasValidLabel(document.label)"
                        @update:model-value="document.label = $event"
                      ></i-text-input>
                    </div>

                    <div class="i-ml-4">
                      <i-text-input
                        :id="`document-max-number-${documentIndex}`"
                        label="Max Docs"
                        placeholder="1"
                        :model-value="document.max_documents"
                        type="number"
                        class="i-form-input"
                        :has-error="!document.max_documents"
                        @update:model-value="document.max_documents = $event"
                      ></i-text-input>
                    </div>

                    <div
                      class="d-flex align-items-center justify-content-center"
                    >
                      <tags-selector
                        class="i-ml-4"
                        title="Select Tags"
                        trigger-locator="button"
                        @input="onTagsChange($event, document)"
                        ><template #trigger>
                          <div>
                            <i-button
                              :id="`collect-tags-selector-trigger-${documentIndex}`"
                              :left-icon="'medium-bookmark-tag'"
                              :size="iButtonSizes.LARGE"
                              :type="iButtonTypes.NEUTRAL"
                              :button-style="iButtonStyles.SOLID_LIGHT"
                            />
                            <i-tooltip
                              :target="`#collect-tags-selector-trigger-${documentIndex}`"
                              title="Manage Tags"
                              :alignment="iTooltipAlignmentMode.TOP_MIDDLE"
                            />
                          </div>
                        </template>
                      </tags-selector>
                      <div>
                        <i-button
                          :id="`collect-doc-request-delete-${documentIndex}`"
                          class="i-ml-4 i-mb-1"
                          :left-icon="'medium-delete-bin-1'"
                          :size="iButtonSizes.LARGE"
                          :type="iButtonTypes.NEUTRAL"
                          :button-style="iButtonStyles.SOLID_LIGHT"
                          @button-click="deleteDocRequest(documentIndex)"
                        />
                        <i-tooltip
                          :target="`#collect-doc-request-delete-${documentIndex}`"
                          title="Delete"
                          :alignment="iTooltipAlignmentMode.TOP_MIDDLE"
                        />
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="documentHasError(document)"
                    class="i-my-4 i-text-body-3 i-text-error-110"
                  >
                    <p v-if="!documentHasValidLabel(document.label)">
                      The document title must have at least two characters
                    </p>
                    <p
                      v-else-if="
                        !documentHasValidMaxDocuments(document.max_documents)
                      "
                    >
                      Please select between 1 and 70 documents
                    </p>
                  </div>
                  <tags :document="document" />

                  <div
                    v-if="liveCaptureEnabled"
                    class="d-flex align-items-center align-items-center i-mt-6"
                  >
                    <i-switch
                      :model-value="document.live_capture_mode"
                      label="Live Image Capture"
                      size="sm"
                      @update:model-value="document.live_capture_mode = $event"
                    />

                    <div class="i-ml-4 live-capture-tooltip">
                      <info-icon
                        title="Live image capture: by enabling live image capture, your
                        customer will have to take a photo of any documents
                        requested from their mobile device."
                      />
                    </div>
                  </div>
                </div>

                <i-button
                  label="Add Document Request"
                  class="full-width i-mb-10"
                  :type="iButtonTypes.NEUTRAL"
                  :button-style="iButtonStyles.SOLID_LIGHT"
                  :size="iButtonSizes.LARGE"
                  @button-click="addDocument()"
                ></i-button>
              </div>
            </div>

            <div v-else>
              <request-card
                icon="medium-file"
                title="Customer Documents"
                description="Send a request for documents"
                @add-request="addDocument"
              ></request-card>
            </div>
          </div>
        </div>

        <div v-else>
          <div v-if="collectSendEmail">
            <i-alert-message
              :type="iAlertMessageType.SUCCESS"
              :size="iAlertMessageSize.SMALL"
              heading="Collect Email Sent"
              :message="`An email has been sent to ${collectEmail}. You can also copy the link below and send it them yourself`"
            ></i-alert-message>
          </div>
          <p v-else>
            <i-alert-message
              :type="iAlertMessageType.SUCCESS"
              :size="iAlertMessageSize.SMALL"
              heading="Collect Link Generated"
              message="Copy the link below and send it to your customer"
            ></i-alert-message>
          </p>
          <h2 class="i-text-subheading-2 i-text-neutral-120 i-mb-4 i-mt-10">
            Collect URL
          </h2>
          <i-copy-input
            element-id="session-url"
            :model-value="collectSession.url"
            container="collect-modal-content"
            @text-copied="onTextCopied"
          ></i-copy-input>
        </div>
      </div>

      <div v-else>
        <i-spinner class="mx-auto i-my-16" size="xl"></i-spinner>
      </div>
    </div>

    <template #footer>
      <i-button
        v-if="!collectSession"
        label="Cancel"
        class="one-third-width i-mr-6"
        :type="iButtonTypes.NEUTRAL"
        :button-style="iButtonStyles.SOLID_LIGHT"
        :size="iButtonSizes.LARGE"
        @button-click="hideCollectModal()"
      ></i-button>

      <i-button
        :disabled="!isValidSession"
        :label="mainCTAText"
        :class="!collectSession ? 'two-thirds-width' : 'full-width'"
        :type="iButtonTypes.PRIMARY"
        :size="iButtonSizes.LARGE"
        @button-click="createSession()"
      ></i-button>
    </template>
  </i-drawer>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import {
  iButton,
  iButtonSizes,
  iButtonStyles,
  iButtonTypes,
  iCopyInput,
  iIcon,
  iSpinner,
  iDrawer,
  iAlertMessage,
  iAlertMessageType,
  iAlertMessageSize,
  iIconToggle,
  iTextInput,
  iTooltip,
  iTooltipAlignmentMode,
  iSwitch,
  showToast,
} from '@inscribe/signal';
import TagsSelector from '@/V2/components/TagsSelector';
import Tags from '@/V2/documents/Tags';
import RequestCard from '@/V2/collect/RequestCard';
import * as request from '@/constants/informationRequestTypes';
import InfoIcon from '@/V2/components/InfoIcon';
import { isValidEmail } from '@/utils/validation';

const BLANK_DOCUMENT = {
  label: '',
  tags: [],
  max_documents: '1',
  live_capture_mode: false,
};

const BLANK_INFORMATION_OPTIONS = [
  request.TYPE_MAP.FULL_NAME,
  request.TYPE_MAP.EMAIL,
  request.TYPE_MAP.EIN,
  request.TYPE_MAP.SSN,
  request.TYPE_MAP.BUSINESS_ADDRESS,
  request.TYPE_MAP.PERSONAL_ADDRESS,
];

export default {
  components: {
    tags: Tags,
    iButton,
    iCopyInput,
    iIcon,
    iSpinner,
    iDrawer,
    iAlertMessage,
    iIconToggle,
    iSwitch,
    iTextInput,
    iTooltip,
    InfoIcon,
    RequestCard,
    TagsSelector,
  },
  props: {
    selectedCustomer: {
      type: Object,
      default: () => {},
    },
    show: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      collectEmail: '',
      collectSendEmail: false,
      collectSession: null,
      collectModalLoading: false,
      informationRequestsAdded: false,
      informationOptions: [...BLANK_INFORMATION_OPTIONS],
      collapseInformationRequestSection: false,
      collapseDocumentRequestSection: false,
      isDragging: false,
      iButtonSizes,
      iButtonStyles,
      iButtonTypes,
      iAlertMessageType,
      iAlertMessageSize,
      iTooltipAlignmentMode,
      documents: [{ ...BLANK_DOCUMENT }],
      text: {
        link: 'Generate Link',
        email: 'Send Email',
      },
      selectedInformationOptions: [],
    };
  },
  computed: {
    ...mapGetters({
      liveCaptureEnabled: 'isCollectLiveCaptureEnabled',
    }),

    isValidSession() {
      const isEmailValid =
        !this.collectSendEmail || (this.collectEmail && !this.hasAnyError);
      const areDocumentsIfAddedValid =
        !this.hasDocumentRequests || !this.documentsHasError;

      const areInformationOptionsIfAddedValid =
        !this.hasInformationRequests ||
        this.selectedInformationOptions.length > 0;

      const atLeastOneRequest =
        this.hasDocumentRequests || this.hasInformationRequests;

      return (
        isEmailValid &&
        atLeastOneRequest &&
        areDocumentsIfAddedValid &&
        areInformationOptionsIfAddedValid &&
        !this.collectModalLoading
      );
    },

    mainCTAText() {
      if (this.collectSession) {
        return 'Done';
      } else {
        return this.collectSendEmail ? this.text.email : this.text.link;
      }
    },

    modalSubheading() {
      return 'Request documents from your customer';
    },

    hasDocumentRequests() {
      return this.documents.length > 0;
    },

    hasInformationRequests() {
      return this.informationRequestsAdded;
    },

    toggleOptions() {
      return [
        {
          value: false,
          icon: 'medium-interface-link',
          text: this.text.link,
          active: !this.collectSendEmail,
        },
        {
          value: true,
          icon: 'medium-mail-envelope',
          text: this.text.email,
          active: this.collectSendEmail,
        },
      ];
    },
    collectEmailHasErrors() {
      return this.collectEmail !== '' && !isValidEmail(this.collectEmail);
    },
    documentsHasError() {
      let hasErrors = false;
      this.documents.forEach((document) => {
        if (this.documentHasError(document)) {
          hasErrors = true;
        }
      });
      return hasErrors;
    },

    hasAnyError() {
      return this.collectEmailHasErrors || this.documentsHasError;
    },
  },
  methods: {
    ...mapActions(['createCollectSession']),

    documentHasError(document) {
      let returnValue = false;
      if (!this.documentHasValidLabel(document?.label)) {
        returnValue = true;
      } else if (!this.documentHasValidMaxDocuments(document?.max_documents)) {
        returnValue = true;
      }
      return returnValue;
    },

    documentHasValidLabel(label) {
      return label === '' || label?.length >= 2;
    },

    documentHasValidMaxDocuments(max_documents) {
      return (
        !max_documents ||
        (parseInt(max_documents) > 0 && parseInt(max_documents) < 71)
      );
    },

    onTagsChange(newTags, document) {
      if (this.isDragging) {
        return;
      }
      document.tags = newTags.map((t) => ({ value: t }));
    },

    addDocument() {
      this.documents = [...this.documents, { ...BLANK_DOCUMENT }];
    },

    toggleInformationRequest() {
      this.informationRequestsAdded = !this.informationRequestsAdded;
    },

    deleteDocRequest(documentIndex) {
      this.documents.splice(documentIndex, 1);
    },

    removeAllDocRequests() {
      this.collapseDocumentRequestSection = false;
      this.documents = [];
    },

    removeAllSelectedInfoRequests() {
      this.informationRequestsAdded = false;
      this.collapseInformationRequestSection = false;
      this.selectedInformationOptions = [];
    },

    onTextCopied() {
      showToast({
        text: 'Link copied',
      });
    },

    hideCollectModal() {
      this.$emit('hide');
      this.collectSession = null;
      this.collectEmail = '';
      this.documents = [{ ...BLANK_DOCUMENT }];
      this.collectSendEmail = false;
      this.collectModalLoading = false;
      this.collapseInformationRequestSection = false;
      this.collapseDocumentRequestSection = false;
      this.removeAllSelectedInfoRequests();
    },

    createSession() {
      if (this.collectSession) {
        this.hideCollectModal();
      } else {
        this.collectModalLoading = true;

        this.createCollectSession({
          document_requests:
            this.documents.length > 0
              ? this.documents.map((document) => ({
                  ...document,
                  tags: document.tags.map((t) => ({ text: t.value })),
                }))
              : undefined,
          customer_information_requests:
            this.selectedInformationOptions.length > 0
              ? this.selectedInformationOptions
              : undefined,
          send_email: this.collectSendEmail,
          customer: {
            id: this.selectedCustomer.id,
            ...(this.collectSendEmail ? { email: this.collectEmail } : {}),
          },
        })
          .then(({ data }) => {
            this.collectSession = data;
            this.collectModalLoading = false;
          })
          .catch((error) => {
            showToast({
              title: 'The collect session could not be created',
              text: error?.response?.data?.message,
              isError: true,
            });
            this.collectModalLoading = false;
            this.hideCollectModal();
          });
      }
    },

    toggleEmail(value) {
      this.collectSendEmail = value;
    },

    toggleCollapse(section) {
      this[section] = !this[section];
    },

    onDragStart(index) {
      this.isDragging = true;
      event.dataTransfer.setData('text/plain', index.toString());
    },
    onDragOver(event, index) {
      event.preventDefault();
    },
    onDrop(event, index) {
      const startIndex = parseInt(event.dataTransfer.getData('text/plain'), 10);
      const droppedDocument = this.documents[startIndex];
      this.documents.splice(startIndex, 1);
      this.documents.splice(index, 0, droppedDocument);
      this.isDragging = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.collect-session-modal {
  .full-width {
    width: 100%;
  }

  .one-third-width {
    width: 33%;
  }

  .two-thirds-width {
    width: 66%;
  }

  .heading-tooltip,
  .live-capture-tooltip {
    button {
      cursor: pointer;
      border: 0;
      padding: 0;
      background: transparent;
      .help-icon {
        color: $light-neutral-80 !important;
      }
      &:focus,
      &:hover {
        .help-icon {
          color: $light-neutral-100 !important;
        }
      }
    }
  }
  .document-max-number {
    width: 120px;
  }

  .information-requests {
    &__options {
      border-bottom: 1px solid $light-neutral-60;
    }

    :deep(.i-radio-checkbox) {
      padding-top: 20px !important;
    }
  }
  .information-requests,
  .document-requests {
    &__heading {
      .actions {
        .remove {
          visibility: hidden;
        }
      }

      &:hover {
        .actions {
          .remove {
            visibility: visible;
          }
        }
      }
    }

    .sortable {
      position: relative;
      margin-left: -38px;
      padding-left: 38px;
      .drag-handle {
        display: none;
        position: absolute;
        justify-content: flex-end;
        width: 40px;
        height: 100%;
        left: -2px;
        cursor: grab;
        .i-icon {
          color: $light-neutral-60 !important;
        }
      }

      &:hover {
        .drag-handle {
          display: flex;
          &:hover {
            .i-icon {
              color: $light-neutral-80 !important;
            }
          }
        }
      }
    }
  }
}

label.i-switch {
  margin-bottom: 0;
}

:deep(.document-tag) {
  margin-right: 4px;
  margin-top: 8px;
}
</style>
